import React from "react";
import { motion } from "framer-motion";
import { Check, Star, Target, Trophy } from "lucide-react";
import { site } from "@/lib/siteContent";

const iconMap = { Star, Target, Trophy };

export default function Partner() {
  const p = site.partner;
  const la = site.lookingAhead;
  return (
    <section
      id="partner"
      data-testid="partner-section"
      className="relative bg-white py-24 lg:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Left: Why partner */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-gold" />
              <span className="text-[11px] font-bold uppercase tracking-[0.32em] text-gold-dark">
                Why Partner
              </span>
            </div>
            <h2 className="font-display text-4xl lg:text-5xl text-navy font-medium leading-[1.05] tracking-tight">
              Why partner <br />
              with <span className="italic text-gold-dark">DSS?</span>
            </h2>

            <ul className="mt-10 space-y-4">
              {p.bullets.map((b, i) => (
                <li key={b} data-testid={`partner-bullet-${i}`} className="flex items-start gap-4 group">
                  <span className="mt-1 shrink-0 w-5 h-5 bg-gold/15 group-hover:bg-gold flex items-center justify-center transition-colors duration-300">
                    <Check size={12} className="text-gold-dark group-hover:text-navy transition-colors duration-300" strokeWidth={3} />
                  </span>
                  <span className="text-base text-navy/80">{b}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Right: Looking ahead */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ delay: 0.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7 relative overflow-hidden bg-navy text-white p-10 lg:p-14"
          >
            {/* Subtle bg image */}
            <img
              src={p.image}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy/95 to-navy-light/80" />

            <div className="relative">
              <div className="text-[11px] font-bold uppercase tracking-[0.32em] text-gold mb-6">
                Looking Ahead
              </div>
              <p className="text-white/85 text-base lg:text-lg leading-relaxed max-w-xl">
                {la.description}
              </p>
              <h3 className="mt-10 font-display text-3xl lg:text-4xl text-white font-medium leading-tight max-w-2xl">
                {la.statement.split(" empowers ")[0]} <span className="italic text-gold">empowers {la.statement.split(" empowers ")[1]}</span>
              </h3>

              <div className="mt-12 grid grid-cols-3 gap-4">
                {la.pillars.map((pl, i) => {
                  const Icon = iconMap[pl.icon] || Star;
                  return (
                    <div
                      key={pl.label}
                      data-testid={`pillar-${i}`}
                      className="border border-white/15 hover:border-gold/50 transition-colors duration-300 p-5 lg:p-6"
                    >
                      <Icon size={24} className="text-gold" strokeWidth={1.5} />
                      <div className="mt-4 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.12em] sm:tracking-[0.22em] text-white break-words leading-tight">
                        {pl.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
